import { MouseEvent } from 'react';
import { FolderIcon } from '@heroicons/react/24/outline';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Hub } from '../../types/hub';
import Card from '../ui/Card';
import QuietAction from '../cards/QuietAction';
import PlaceLink from '../layout/PlaceLink';
import SaveToMyOrbconButton from '../saved/SaveToMyOrbconButton';
import { resourceWord } from '../../utils/cardFacts';
import { hubPlaceHref } from '../../utils/hubChapter';
import { placePath } from '../../utils/placePaths';
import { openPlace } from '../../utils/placeMotion';

export interface HubRowData {
  id: string;
  title: string;
  slug: string;
  resources: Array<{ id: string }>;
  program?: Hub['program'];
}

interface HubRowProps {
  hub: HubRowData;
  /** Header search uses its own place transition so the panel can close. */
  search?: boolean;
  /** Drop the row's own border when it sits inside an existing list. */
  embedded?: boolean;
  onActivate?: (origin: HTMLElement) => void;
}

export default function HubRow({ hub, search = false, embedded = false, onActivate }: HubRowProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const count = resourceWord(hub.resources.length);
  const program = hub.program?.slug ? hub.program : null;
  const programTo = program ? placePath(program) : '';
  const to = hubPlaceHref(hub);

  if (search) {
    const row = (
      <div
        className={`flex w-full overflow-hidden bg-white ${
          embedded ? '' : 'border border-gray-200 rounded-lg shadow-sm'
        } min-w-[28rem]`}
      >
        <PlaceLink
          to={to}
          surface={{ kind: 'hub', slug: hub.slug, via: 'search' }}
          ariaLabel={hub.title}
          onClick={(event: MouseEvent<HTMLAnchorElement>) => {
            if (!onActivate) return;
            event.preventDefault();
            onActivate(event.currentTarget);
          }}
          className="flex min-w-0 flex-1 items-stretch text-left focus-ring min-h-[44px]"
        >
          <div className="bg-primary/10 text-primary shrink-0 py-3 flex flex-col items-center justify-center text-center w-[5.5rem] min-w-[5.5rem] max-w-[5.5rem] px-2">
            <FolderIcon className="h-5 w-5" aria-hidden />
            <span className="mt-1 text-sm font-medium">{count}</span>
          </div>
          <div className="min-w-0 flex-1 px-4 py-4">
            <p className="text-sm font-medium text-gray-600">Toolkit</p>
            <p className="text-base font-semibold text-gray-900 whitespace-nowrap">{hub.title}</p>
            {program && <p className="mt-1 text-sm text-gray-600">In {program.title}</p>}
          </div>
        </PlaceLink>
        <div className="px-4 py-4">
          <SaveToMyOrbconButton
            type="HUB"
            hubId={hub.id}
            title={hub.title}
            slug={hub.slug}
            variant="icon"
            align="end"
          />
        </div>
      </div>
    );
    return <div className="overflow-x-auto">{row}</div>;
  }

  return (
    <Card className="!p-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-600">Toolkit</p>
          <h3 className="mt-0.5 text-base font-semibold text-gray-900">{hub.title}</h3>
        </div>
        <SaveToMyOrbconButton
          type="HUB"
          hubId={hub.id}
          title={hub.title}
          slug={hub.slug}
          variant="icon"
          align="end"
        />
      </div>
      {program && (
        <p className="mt-1.5 text-sm">
          <Link
            to={programTo}
            onClick={(event) => {
              openPlace(event, event.currentTarget, { kind: 'program', slug: program.slug }, programTo, navigate, location);
            }}
            className="font-medium text-primary hover:text-primary-dark focus-ring rounded-sm"
          >
            In {program.title}
          </Link>
        </p>
      )}
      <p className="mt-1.5 text-sm text-gray-600">{count}</p>
      <div className="mt-2">
        <span className="inline-flex min-h-[44px] items-center">
          <QuietAction
            to={to}
            label={`Open ${hub.title}`}
            onClick={(event) => {
              if (onActivate) {
                event.preventDefault();
                onActivate(event.currentTarget);
                return;
              }
              openPlace(event, event.currentTarget, { kind: 'hub', slug: hub.slug, via: 'link' }, to, navigate, location);
            }}
          >
            Open
          </QuietAction>
        </span>
      </div>
    </Card>
  );
}
